'use client';
import { SessionContext } from '@benzinga/session-context';
import styled from '@benzinga/themetron';
import React from 'react';
import {
  AutocompleteModule,
  ActionModule,
  SearchModule,
  TagModule,
  ClipboardModule,
  QueryScore,
  isNoCommaString,
} from './Modules';
import { AutocompleteManager } from '@benzinga/autocomplete-manager';
import MiniSnapshot, { RenderSnapshot } from '../../../utils/MiniSnapshot';
import { PerformanceColor } from '../../Ticker';
import { ResultDescription, ResultHead } from '../Expression/ExpressionSearch';
import { SearchContext, SearchContextType } from '../context';
import { TrendingTopicsManager } from '@benzinga/trending-topics-manager';
import {
  SearchItem,
  SymbolModuleId,
  SymbolSearchItem,
  SymbolSearchItemData,
  SymbolSearchItemVersion,
  createSymbolSearchItem,
  isLegacySymbolSearchItem,
  isSymbolSearchItem,
  SymbolItemSpecifier,
} from '@benzinga/search-modules';
import { WidgetContext, WidgetId } from '@benzinga/widget-tools';
import Styles from '../styles/styles.module.scss';

export interface SymbolSearchModule extends SearchModule<SymbolSearchItemData> {
  id: typeof SymbolModuleId;
  autocomplete: AutocompleteModule<SymbolSearchItemData>;
  tag: TagModule;
  action: ActionModule;
  clipboard: ClipboardModule;
}

const renderMiniSnapshot = (_symbol: string, currentPrice: string, nominalChange: number, percentChange: number) => (
  <span>
    <StockResultsPrice>{currentPrice}</StockResultsPrice>
    {nominalChange !== 0 && (
      <>
        <StockResultsNominalChange>
          <PerformanceColor hasArrow stat={nominalChange} />
        </StockResultsNominalChange>
        <span className="percentChange">
          <PerformanceColor isPercentage stat={percentChange} />
        </span>
      </>
    )}
  </span>
);

const SymbolDropdown: (item: SymbolSearchItem) => React.FC<{ isSelected: boolean; isLocked?: boolean }> =
  (item: SymbolSearchItem) => props => {
    const companyName = item.data.label.split('|')[1]?.trim();
    return (
      <>
        <ResultHead $isSelected={props.isSelected} className={Styles.resultHead}>
          {item.data.isTrending ? 'Trending Symbol' : 'Symbol'}
        </ResultHead>
        <div className={Styles.symbolWrapper}>
          <ResultDescription $isSelected={props.isSelected} className={Styles.resultDescription}>
            {item.data.symbol}
          </ResultDescription>
          <StockResultsQuotes className={Styles.noWrap}>
            <MiniSnapshot render={renderMiniSnapshot as unknown as RenderSnapshot} symbol={item.data.symbol} />
          </StockResultsQuotes>
          <ResultDescription $isSelected={props.isSelected} className={Styles.resultDescription}>
            {companyName}
          </ResultDescription>
        </div>
      </>
    );
  };
export const createSymbolAutocompleteModule = (
  autocompleteManager: AutocompleteManager,
  trendingTopicsManager: TrendingTopicsManager,
  searchContext: SearchContextType,
  widgetId: WidgetId | undefined,
): AutocompleteModule<SymbolSearchItemData> => {
  const messenger = autocompleteManager.getMessenger();
  return {
    dropdown: (item: SearchItem) => {
      if (!isSymbolSearchItem(item)) return undefined;
      return {
        component: SymbolDropdown(item),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: SymbolSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isSymbolSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit: number, selectedItems?: SearchItem[]) => {
      let canceled = false;
      const moduleSelectedItems = selectedItems?.flatMap(item => (isSymbolSearchItem(item) ? [item] : [])) ?? [];
      return {
        cancel: () => {
          canceled = true;
        },
        promise: new Promise(resolve => {
          (async () => {
            if (text === '') {
              const trendingTopics = await trendingTopicsManager
                .getTrendingTopics(['tickers'])
                .then(topics => (topics.ok ? topics.ok : []));

              const trendingTopicSymbols = (
                await Promise.all(
                  trendingTopics.map<Promise<[SearchItem<SymbolSearchItemData>, QueryScore] | undefined>>(topic => {
                    return messenger.search(topic.name, false, true).then(result => {
                      if (result.canceled || canceled) return undefined;
                      else if (result.err) return undefined;
                      else if (result.ok.length < 1) return undefined;
                      else
                        return [
                          createSymbolSearchItem(result.ok[0].tagId, result.ok[0].label, true),
                          QueryScore.StartsWithMatch,
                        ];
                    });
                  }),
                )
              ).flatMap(item => (item ? [item] : []));
              if (canceled) return resolve({ canceled: true });
              const recentlyUsedSymbols = searchContext
                .getRecentModuleItem(widgetId ?? '')
                ?.filter(isSymbolSearchItem)
                .map<[SearchItem<SymbolSearchItemData>, QueryScore]>(item => [item, QueryScore.HistoryMatch]);

              const lastUsedSymbol = recentlyUsedSymbols
                .slice(0, 1)
                .map<[SearchItem<SymbolSearchItemData>, QueryScore]>(item => [item[0], QueryScore.Priority]);

              return resolve({
                canceled: false,
                ok: [
                  ...lastUsedSymbol,
                  ...trendingTopicSymbols,
                  ...recentlyUsedSymbols,
                  ...defaultTickers.map<[SearchItem<SymbolSearchItemData>, QueryScore]>(item => [
                    item,
                    QueryScore.NoMatch,
                  ]),
                ]
                  .filter(
                    item => !moduleSelectedItems.some(selectedItem => selectedItem.data.symbol === item[0].data.symbol),
                  )
                  .filter((item, index, arr) =>
                    arr.every(
                      (selectedItem, index2) => index2 >= index || selectedItem[0].data.symbol !== item[0].data.symbol,
                    ),
                  )
                  .slice(0, limit),
              });
            } else {
              messenger.search(text, false, true).then(result => {
                if (result.canceled || canceled) return resolve({ canceled: true });
                else if (result.err) return resolve(result as unknown as any);
                else
                  return resolve({
                    canceled: false,
                    ok: result.ok
                      .filter(
                        item => !moduleSelectedItems.some(selectedItem => selectedItem.data.symbol === item.tagId),
                      )
                      .slice(0, limit)
                      .map<[SearchItem<SymbolSearchItemData>, QueryScore]>(item => [
                        createSymbolSearchItem(item.tagId, item.label),
                        item.tagId.toLowerCase() === text.toLowerCase()
                          ? QueryScore.FullMatch
                          : item.tagId.toLowerCase().startsWith(text.toLowerCase())
                            ? QueryScore.StartsWithMatch
                            : QueryScore.IncludesMatch,
                      ]),
                  });
              });
            }
          })();
        }),
      };
    },
    specifier: SymbolItemSpecifier,
  };
};

export const createSymbolTagModule = (): TagModule => {
  return {
    render: item => {
      if (!isSymbolSearchItem(item)) return undefined;
      return {
        text: item.data.symbol,
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isSymbolSearchItem(item)) return undefined;
      return item.data.symbol;
    },
  };
};

export const createSymbolActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createSymbolClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isSymbolSearchItem(token)) return undefined;
      return [token];
    },
    onCopyString: token => {
      if (!isSymbolSearchItem(token)) return undefined;
      const str = `${SymbolItemSpecifier}${token.data.symbol}`;
      return isNoCommaString(str) ? [str] : undefined;
    },
    onPasteJSON: token => {
      if (isLegacySymbolSearchItem(token)) return SymbolModuleItemLoader(token);
      return undefined;
    },
    onPasteString: str => {
      if (str.startsWith(SymbolItemSpecifier)) {
        return createSymbolSearchItem(str.slice(SymbolItemSpecifier.length));
      }
      return undefined;
    },
  };
};

const SymbolModuleItemLoader = (item: SearchItem) => {
  if (!isLegacySymbolSearchItem(item)) return undefined;

  switch (item.version) {
    case SymbolSearchItemVersion:
    default:
      return item;
  }
};

export const createSymbolSearchModule = (
  autocompleteManager: AutocompleteManager,
  trendingTopicsManager: TrendingTopicsManager,
  searchContext: SearchContextType,
  widgetId: WidgetId,
): SymbolSearchModule => {
  return {
    action: createSymbolActionModule(),
    autocomplete: createSymbolAutocompleteModule(
      autocompleteManager,
      trendingTopicsManager,
      searchContext,
      widgetId ?? '',
    ),
    clipboard: createSymbolClipboardModule(),
    id: SymbolModuleId,
    loader: SymbolModuleItemLoader,
    tag: createSymbolTagModule(),
  };
};

export const useSymbolSearchModule = () => {
  const session = React.useContext(SessionContext);
  const searchContext = React.useContext(SearchContext);
  const autocompleteManager = session.getManager(AutocompleteManager);
  const trendingTopicsManager = session.getManager(TrendingTopicsManager);
  const widgetContext = React.useContext(WidgetContext);
  return React.useMemo(
    () =>
      createSymbolSearchModule(autocompleteManager, trendingTopicsManager, searchContext, widgetContext.widgetId ?? ''),
    [autocompleteManager, trendingTopicsManager, searchContext, widgetContext.widgetId],
  );
};

const StockResultsQuotes = styled.div`
  display: flex;
`;

const StockResultsPrice = styled.span`
  margin: 0 !important;
`;

const StockResultsNominalChange = styled.span`
  margin: 0 8px;
`;

const defaultTickers = Object.freeze([
  createSymbolSearchItem('AAPL', 'AAPL | Apple'),
  createSymbolSearchItem('TSLA', 'TSLA | Tesla'),
  createSymbolSearchItem('AMZN', 'AMZN | Amazon.com'),
  createSymbolSearchItem('META', 'META | Meta Platforms'),
  createSymbolSearchItem('GOOG', 'GOOG | Alphabet'),
  createSymbolSearchItem('MSFT', 'MSFT | Microsoft'),
  createSymbolSearchItem('NVDA', 'NVDA | NVIDIA'),
  createSymbolSearchItem('DIS', 'DIS | Walt Disney'),
  createSymbolSearchItem('BTC/USD', 'BTC/USD | Bitcoin - United States Dollar'),
  createSymbolSearchItem('JPM', 'JPM | JPMorgan Chase'),
]);
